import { useState, useRef, useCallback, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Trash, Upload, Image as ImageIcon, DotsSixVertical } from "@phosphor-icons/react";

export default function EnhancedPhotoUploader({ entries = [], onChange, categories = [] }) {
  const [uploading, setUploading] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({});
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const loadPhoto = useCallback(async (pid) => {
    try {
      const resp = await axios.get(`${API}/photos/${pid}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setPhotoUrls(prev => ({ ...prev, [pid]: url }));
    } catch {}
  }, []);

  useEffect(() => {
    entries.forEach(e => { if (e.id && !photoUrls[e.id]) loadPhoto(e.id); });
  }, [entries, loadPhoto, photoUrls]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/photos/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const newEntry = { id: data.id, caption: file.name.replace(/\.[^/.]+$/, ""), category: categories[0] || "Other" };
      onChange([...entries, newEntry]);
      setTimeout(() => loadPhoto(data.id), 200);
      toast.success(`Photo uploaded: ${file.name}`);
    } catch {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files) => {
    Array.from(files).forEach(f => { if (f.type.startsWith("image/")) uploadFile(f); });
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };

  const updateEntry = (idx, field, value) => {
    const updated = [...entries];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  };

  const removeEntry = async (idx) => {
    const entry = entries[idx];
    try { await axios.delete(`${API}/photos/${entry.id}`); } catch {}
    if (photoUrls[entry.id]) URL.revokeObjectURL(photoUrls[entry.id]);
    onChange(entries.filter((_, i) => i !== idx));
  };

  // Drag reorder
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOverItem = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDropItem = (e, dropIdx) => {
    e.preventDefault(); e.stopPropagation();
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...entries];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    onChange(reordered);
    setDragIdx(null); setDragOverIdx(null);
  };

  return (
    <div data-testid="enhanced-photo-uploader">
      <div
        className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50 hover:border-blue-400"}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid="photo-dropzone"
      >
        <input ref={fileInputRef} type="file" className="hidden" accept="image/*" multiple onChange={(e) => handleFiles(e.target.files)} />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-6 h-6 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-slate-500">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload size={28} weight="light" className="text-slate-400" />
            <p className="text-sm text-slate-600"><span className="font-semibold text-blue-900">Click to upload</span> or drag and drop</p>
          </div>
        )}
      </div>

      {entries.length > 0 && (
        <div className="space-y-3 mt-4">
          {entries.length > 1 && <p className="text-xs text-slate-400">Drag to reorder photos</p>}
          {entries.map((entry, idx) => (
            <div
              key={entry.id || idx}
              draggable={entries.length > 1}
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOverItem(e, idx)}
              onDrop={(e) => handleDropItem(e, idx)}
              onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
              className={`flex gap-3 items-start p-3 border rounded-md transition-all ${
                dragOverIdx === idx && dragIdx !== idx ? "border-blue-500 ring-2 ring-blue-200" : dragIdx === idx ? "opacity-50 border-slate-300" : "border-slate-200 bg-white"
              }`}
              data-testid={`photo-entry-${idx}`}
            >
              {entries.length > 1 && (
                <div className="pt-2 cursor-grab active:cursor-grabbing"><DotsSixVertical size={16} className="text-slate-400" /></div>
              )}
              <div className="w-24 h-16 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                {photoUrls[entry.id] ? (
                  <img src={photoUrls[entry.id]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ImageIcon size={20} className="text-slate-300" /></div>
                )}
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input
                  value={entry.caption || ""}
                  onChange={(e) => updateEntry(idx, "caption", e.target.value)}
                  placeholder="Caption"
                  className="h-8 text-sm border-slate-200"
                />
                <Select value={entry.category || ""} onValueChange={(v) => updateEntry(idx, "category", v)}>
                  <SelectTrigger className="h-8 text-sm border-slate-200"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{idx + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeEntry(idx)} className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"><Trash size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
